import { z } from 'zod';
/**
 * "Was this lesson useful?" — the learner's answer on the completion screen, and what the
 * console reads back out of it.
 *
 * Not in the package barrel — import `@platform/schema/feedback`. Same reason as
 * `scene-tools`: the barrel is on the mobile boot path and Metro does not tree-shake.
 */
/** Why a lesson was not useful. A closed set, several at once, in the card's order. */
export const feedbackReasonSchema = z
    .enum([
    'CONFUSING',
    'TOO_BASIC',
    'TOO_DIFFICULT',
    'NOT_RELEVANT',
    'SOMETHING_WRONG',
])
    .describe('Why the lesson was not useful.');
/** What the learner is offered, and what the console calls each one. */
export const FEEDBACK_REASON_LABEL = {
    CONFUSING: 'Too confusing',
    TOO_BASIC: 'Too basic',
    TOO_DIFFICULT: 'Too difficult',
    NOT_RELEVANT: 'Not relevant to me',
    SOMETHING_WRONG: 'Something was wrong',
};
export const appPlatformSchema = z
    .enum(['ios', 'android', 'web'])
    .describe('Where the app is running.');
export const submitLessonFeedbackSchema = z
    .strictObject({
    helpful: z.boolean().describe('Yes, or Not really.'),
    reasons: z
        .array(feedbackReasonSchema)
        .max(feedbackReasonSchema.options.length)
        .optional()
        .describe('Only with `helpful: false`, and optional there.'),
    appVersion: z
        .string()
        .min(1)
        .max(80)
        .describe('The build the learner is on — the app sends `release`.'),
    platform: appPlatformSchema.optional(),
})
    .check((ctx) => {
    const { helpful, reasons = [] } = ctx.value;
    if (helpful && reasons.length > 0)
        ctx.issues.push({
            code: 'custom',
            input: ctx.value,
            path: ['reasons'],
            message: 'Reasons go with a lesson that was not useful.',
        });
    if (new Set(reasons).size !== reasons.length)
        ctx.issues.push({
            code: 'custom',
            input: ctx.value,
            path: ['reasons'],
            message: 'Each reason once.',
        });
})
    .describe('PUT /attempts/:id/feedback');
export const lessonFeedbackSchema = z
    .strictObject({
    helpful: z.boolean(),
    reasons: z.array(feedbackReasonSchema),
    lessonVersion: z.number().int().min(1),
    answeredAt: z.iso.datetime(),
})
    .describe('The learner’s answer for this lesson version. A later one replaces it.');
/**
 * Below this many responses a lesson has no helpful rate — too few to call it weak.
 * The console's date window lifts it: a short window is asked for on purpose.
 */
export const FEEDBACK_MIN_RESPONSES = 5;
/** What the console shows of one lesson version's feedback. */
export const feedbackTallySchema = z
    .strictObject({
    count: z.number().int().min(0),
    helpful: z.number().int().min(0).describe('Yes answers.'),
    notHelpful: z.number().int().min(0).describe('Not really answers.'),
    helpfulRate: z
        .number()
        .min(0)
        .max(1)
        .optional()
        .describe(`Yes over all answers. Absent with no answers, and below ${String(FEEDBACK_MIN_RESPONSES)} outside a date window — insufficient data.`),
    topReason: feedbackReasonSchema
        .optional()
        .describe('The reason picked most often. Ties go to the earlier one.'),
    lastAt: z.iso.datetime().optional().describe('The latest answer.'),
})
    .describe('Feedback on one lesson version.');
export const versionFeedbackSchema = feedbackTallySchema
    .extend({
    version: z.number().int().min(1),
    reasons: z
        .array(z.strictObject({
        reason: feedbackReasonSchema,
        count: z.number().int().min(1),
    }))
        .describe('Every reason picked, most often first.'),
})
    .describe('One published version of the lesson and what learners said of it.');
/**
 * An answer the console may put a name to: its learner has sent the team a support
 * message, which is where the name and email come from.
 */
export const namedFeedbackSchema = z
    .strictObject({
    name: z.string().min(1),
    email: z.string().min(1),
    helpful: z.boolean(),
    reasons: z.array(feedbackReasonSchema),
    lessonVersion: z.number().int().min(1),
    scorePct: z.number().int().min(0).max(100),
    answeredAt: z.iso.datetime(),
})
    .describe('One learner’s answer, with who they are.');
export const lessonFeedbackReportSchema = z
    .strictObject({
    version: z.number().int().min(1).describe('The live version.'),
    versions: z
        .array(versionFeedbackSchema)
        .describe('Newest first, only versions with answers.'),
    named: z
        .array(namedFeedbackSchema)
        .max(100)
        .describe('Latest first. Only learners who have sent a support message; everyone else stays in the totals.'),
})
    .describe('GET /authoring/lessons/:id/feedback');
//# sourceMappingURL=feedback.js.map